import React, { useEffect, useState } from "react";
import { Table, Typography, Tooltip, Button, Row, Col, Modal, Select, Input, message, Popconfirm, Spin, Tree, Card } from 'antd';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import { useNavigate } from "react-router-dom";
import { DeleteOutlined, EditOutlined, CopyOutlined, PlusOutlined, MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { useSelector } from "react-redux";

const { Title } = Typography;
const { Option } = Select;

const Roles = () => {

    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [roleName, setRoleName] = useState(null);
    const [createRole, setCreateRole] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [selectedParentRole, setSelectedParentRole] = useState(null);
    const security = useSelector((state) => state.data.items[0]);

    const navigate = useNavigate();


    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const apiServiceForRoles = new ApiService(
                `${BASE_URL}/get_all_user_role`,
                { 'Content-Type': 'application/json' },
                'GET'
            );
            const rolesData = await apiServiceForRoles.makeCall();
            setRoles(rolesData || []);
        }
        catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const handleEditRole = async (role) => {
        if(security?.system_settings?.user_role?.edit===false){
                  message.error('You do not have permission to edit ');
                  return;
        }
        setSelectedRole(role);
        setRoleName(role?.Name);
        setSelectedParentRole(role?.reports_to_id || null);
        setCreateRole(true);
    }

    const deleteRole = async (role) => {
        if(security?.system_settings?.user_role?.delete===false){
                  message.error('You do not have permission to delete ');
                  return;
        }
        try {
            const apiServiceForRoles = new ApiService(
                `${BASE_URL}/delete_user_role/${role?._id}`,
                { 'Content-Type': 'application/json' },
                'DELETE'
            );
            const rolesData = await apiServiceForRoles.makeCall();
            fetchRoles();

        } catch (error) {

        }
    }

    const handleCreateRole = (parentRole = null) => {
         if(security?.system_settings?.user_role?.create===false){
                  message.error('You do not have permission to create ');
                  return;
        }
        setRoleName(null);
        setSelectedRole(null);
        setSelectedParentRole(parentRole ? parentRole._id : null);
        setCreateRole(true);
    };


    const handleSave = async () => {
        if (!roleName) {
            message.error("Please fill all the details");
            return;
        }
        if (selectedRole && selectedParentRole === selectedRole._id) {
            message.error("A role cannot report to itself.");
            return;
        }
        try {
            setLoading(true);
            let apiService = '';
            if (selectedRole?._id) {
                selectedRole.Name = roleName;
                selectedRole.reports_to_id = selectedParentRole;

                apiService = new ApiService(
                    `${BASE_URL}/update_user_role/${selectedRole?._id}`,
                    { 'Content-Type': 'application/json' },
                    'PATCH', selectedRole
                );
            } else {
                const body = {
                    mt_user_role: {
                        Name: roleName,
                        reports_to_id: selectedParentRole
                    }
                }
                apiService = new ApiService(
                    `${BASE_URL}/create_user_role`,
                    { 'Content-Type': 'application/json' },
                    'POST', body
                );
            }

            const response = await apiService.makeCall();
            message.success('Role added successfully');
            setSelectedRole(null);
            fetchRoles();

        }
        catch (error) {
            console.log(error);
        } finally {
            setCreateRole(false);
            setRoleName(null);
            setSelectedParentRole(null);
            setLoading(false);
        }
    }

    const handleCancel = () => {
        setCreateRole(false);
        setRoleName(null);
        setSelectedParentRole(null);
    };

    const buildTree = (roles) => {
        const roleMap = new Map();
        roles.forEach(role => {
            roleMap.set(role._id, { ...role, children: [] });
        });

        const treeData = [];
        roles.forEach(role => {
            if (!role.reports_to_id) {
                // Root role
                treeData.push(roleMap.get(role._id));
            } else {
                const parentRole = roleMap.get(role.reports_to_id);
                if (parentRole) {
                    parentRole.children.push(roleMap.get(role._id));
                }
            }
        });
        return treeData;
    };


    const formatTreeData = (nodes) => {
        return nodes?.map(node => ({
            title: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px' }}>{node.Name}</span>
                    <div>
                        <Tooltip title="Edit Role">
                            <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditRole(node)}
                            />
                        </Tooltip>
                        <Tooltip title="Delete Role">
                            <Popconfirm
                                title="Are you sure to delete this role?"
                                onConfirm={() => deleteRole(node)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </Tooltip>
                        <Tooltip title="Add Sub-Role">
                            <Button
                                type="link"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => handleCreateRole(node)} // Pass node as parentRole
                            />
                        </Tooltip>
                    </div>
                </div>
            ),
            key: `role-${node?._id}`,
            children: formatTreeData(node?.children || []),
        }));
    };


    useEffect(() => {
        setTreeData(formatTreeData(buildTree(roles)));
    }, [roles]);

    const getAllKeys = (nodes) => {
        let keys = [];
        nodes.forEach((node) => {
            keys.push(node.key);
            if (node.children) {
                keys = keys.concat(getAllKeys(node.children));
            }
        });
        return keys;
    };

    const handleExpandAll = () => {
        setExpandedKeys(getAllKeys(treeData));
    };

    const handleCloseAll = () => {
        setExpandedKeys([]);
    };


    return (
        <div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
                    <Col>
                        <Title level={4} style={{ margin: 0 }}>
                            Your Organization's Role Hierarchy
                        </Title>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={() => handleCreateRole()}>
                            Set Up a new Role
                        </Button>
                    </Col>
                </Row>
            </div>

            <Spin spinning={loading}>
                {treeData?.length !== 0 && (
                <Card bordered style={{ margin: "16px", padding: "16px" }}>
                    <div style={{ marginBottom: "8px" }}>
                        <span
                            style={{ textDecoration: "underline", cursor: "pointer", marginRight: "16px" }}
                            onClick={handleCloseAll}
                        >
                            Collapse All

                        </span>
                        <span
                            style={{ textDecoration: "underline", cursor: "pointer" }}
                            onClick={handleExpandAll}
                        >
                            Expand All

                        </span>
                    </div>
                    <Tree
                        treeData={treeData}
                        expandedKeys={expandedKeys}
                        onExpand={(keys) => setExpandedKeys(keys)}
                        showLine
                        switcherIcon={({ expanded }) =>
                            expanded ? (
                                <MinusSquareOutlined style={{ fontSize: "14px" }} />
                            ) : (
                                <PlusSquareOutlined style={{ fontSize: "14px" }} />
                            )
                        }
                    />
                </Card>
                )}
            </Spin>
            <Modal
                title="Create new Role"
                visible={createRole}
                onCancel={handleCancel}
                footer={null}
                width={600}
                zIndex={1002}   // higher than sidebar
            >
                <Spin spinning={loading}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <div style={{ marginBottom: '16px' }}>
                                <label>Name</label>
                                <Input
                                    value={roleName}
                                    onChange={(e) => setRoleName(e.target.value)}
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </Col>
                        <Col span={24}>
                            <div style={{ marginBottom: '16px' }}>
                                <label>Report To</label>
                                <Select
                                    value={selectedParentRole}
                                    onChange={(value) => setSelectedParentRole(value)}
                                    style={{ width: '100%' }}
                                    allowClear
                                >
                                    {roles
                                        .filter(role => selectedRole?._id !== role._id)
                                        .map((role) => (
                                            <Option key={role?._id} value={role?._id}>
                                                {role?.Name}
                                            </Option>
                                        ))}
                                </Select>
                            </div>
                        </Col>

                    </Row>
                    <Row justify="end">
                        <Col>
                            <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                                Cancel
                            </Button>
                            <Button type="primary" onClick={handleSave}>
                                Save
                            </Button>
                        </Col>
                    </Row>
                </Spin>
            </Modal>
        </div>

    );
}

export default Roles;